import React from 'react';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(() => ({
  root: {
    padding: '12px 16px',
    background: '#f5f5f5',
    borderTop: '1px solid #e0e0e0',
    flexShrink: 0,
  },
  header: {
    fontSize: 11,
    fontWeight: 600,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 8,
  },
  progressRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  progressLabel: {
    fontSize: 13,
    fontWeight: 500,
    color: '#333',
  },
  progressBar: {
    flex: 1,
    height: 5,
    background: '#e0e0e0',
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
    background: '#4caf50',
    transition: 'width 0.3s ease',
  },
  progressCount: {
    fontSize: 11,
    color: '#888',
    flexShrink: 0,
  },
  divider: {
    height: 1,
    background: '#e0e0e0',
    margin: '8px 0',
  },
  statsGrid: {
    display: 'flex',
    gap: 12,
  },
  stat: {
    flex: 1,
    textAlign: 'center',
  },
  statValue: {
    fontSize: 15,
    fontWeight: 600,
    color: '#333',
    lineHeight: 1.2,
  },
  statLabel: {
    fontSize: 10,
    color: '#999',
    marginTop: 2,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  statEmpty: {
    color: '#ccc',
  },
  skippedNote: {
    fontSize: 11,
    color: '#bbb',
    marginTop: 6,
    textAlign: 'center',
  },
}));

/**
 * Format a number with thousands separators, truncating decimals.
 */
function formatNum(n) {
  if (!n || n === 0) return '—';
  return parseFloat(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/**
 * MeasurementSummary — compact footer showing placement progress and totals.
 *
 * Props:
 *   summary – {
 *     totalPlaced:  number,
 *     totalQueued:  number,
 *     totalSkipped: number,
 *     sfTotal:      number,
 *     lfTotal:      number,
 *     pointsTotal:  number,
 *   }
 */
const MeasurementSummary = ({ summary }) => {
  const classes = useStyles();

  if (!summary) return null;

  const { totalPlaced, totalQueued, totalSkipped, sfTotal, lfTotal, pointsTotal } = summary;
  const totalItems = totalPlaced + totalQueued + totalSkipped;
  const progressPct = totalItems > 0 ? (totalPlaced / totalItems) * 100 : 0;

  const hasMeasurements = sfTotal > 0 || lfTotal > 0 || pointsTotal > 0;

  return (
    <div className={classes.root}>
      <div className={classes.header}>Progress</div>

      {/* Progress bar */}
      <div className={classes.progressRow}>
        <span className={classes.progressLabel}>
          Placed
        </span>
        <div className={classes.progressBar}>
          <div
            className={classes.progressFill}
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <span className={classes.progressCount}>
          {totalPlaced}/{totalItems}
        </span>
      </div>

      {hasMeasurements && (
        <>
          <div className={classes.divider} />

          <div className={classes.statsGrid}>
            <div className={classes.stat}>
              <div className={`${classes.statValue} ${sfTotal === 0 ? classes.statEmpty : ''}`}>
                {formatNum(sfTotal)}
              </div>
              <div className={classes.statLabel}>SF</div>
            </div>

            <div className={classes.stat}>
              <div className={`${classes.statValue} ${lfTotal === 0 ? classes.statEmpty : ''}`}>
                {formatNum(lfTotal)}
              </div>
              <div className={classes.statLabel}>LF</div>
            </div>

            <div className={classes.stat}>
              <div className={`${classes.statValue} ${pointsTotal === 0 ? classes.statEmpty : ''}`}>
                {formatNum(pointsTotal)}
              </div>
              <div className={classes.statLabel}>Points</div>
            </div>
          </div>
        </>
      )}

      {totalSkipped > 0 && (
        <div className={classes.skippedNote}>
          {totalSkipped} item{totalSkipped !== 1 ? 's' : ''} skipped
        </div>
      )}
    </div>
  );
};

export default MeasurementSummary;
